import {
  formatManualJobSelectValue,
  MANUAL_WELDING_MACHINE_ID,
  parseManualJobSelectValue,
} from './manualJobNumberFormat';
import {
  isManualStatusWeldParameter,
  mapApiResponseToJobNumbers,
  mapLimitsDataToManualStatusConfiguration,
  type LimitsDataForManualStatus,
} from './manualStatusConfigurationMappers';

export type CombinedStatusJobOption = {
  label: string;
  value: string;
  jobNumber: string;
  pipeName: string;
  isManual: boolean;
};

export type AutomatedStatusJobOption = {
  label: string;
  value: string;
  jobNumber: string;
  pipeName: string;
};

const normalizePipeName = (pipeName?: unknown): string => {
  if (pipeName == null || pipeName === 'null') return '';
  return String(pipeName);
};

const normalizeWeldingSystemLabel = (value?: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/\W/g, '');

const jobKey = (jobNumber: string, pipeName = '') => `${jobNumber}:${pipeName}`;

/** Automated jobs from get-limits (`allDataSet` + non-manual `weldDetails`). */
export function buildAutomatedJobOptionsFromLimits(
  limitsData: LimitsDataForManualStatus | null | undefined
): AutomatedStatusJobOption[] {
  const allDataSet = limitsData?.allDataSet ?? [];
  const weldDetails = limitsData?.weldDetails ?? [];
  const jobEntries = new Map<string, { jobNumber: string; pipeName: string }>();

  const registerJob = (jobNumber: unknown, pipeName?: unknown) => {
    if (!jobNumber) return;
    const normalizedJobNumber = String(jobNumber).trim();
    if (!normalizedJobNumber) return;
    const normalizedPipeName = normalizePipeName(pipeName);
    jobEntries.set(jobKey(normalizedJobNumber, normalizedPipeName), {
      jobNumber: normalizedJobNumber,
      pipeName: normalizedPipeName,
    });
  };

  allDataSet.forEach((item) => {
    registerJob(item.job_number, item.pipe_name);
  });

  weldDetails.forEach((detail) => {
    if (isManualStatusWeldParameter(detail)) return;
    registerJob(detail.job_number, detail.pipe_name);
  });

  return Array.from(jobEntries.values()).map(({ jobNumber, pipeName }) => {
    const weldDetail =
      weldDetails.find(
        (detail) =>
          detail.job_number == jobNumber &&
          normalizePipeName(detail.pipe_name) === pipeName
      ) ?? weldDetails.find((detail) => detail.job_number == jobNumber);

    const tableItem =
      allDataSet.find(
        (item) =>
          item.job_number === jobNumber &&
          normalizePipeName(item.pipe_name) === pipeName
      ) ??
      allDataSet.find(
        (item) =>
          item.job_number === jobNumber &&
          (!pipeName || normalizePipeName(item.pipe_name) === '')
      );

    const weldingSystem =
      normalizeWeldingSystemLabel(weldDetail?.welding_system) ||
      normalizeWeldingSystemLabel(tableItem?.welding_system);

    const displayValue = `${weldingSystem}: ${jobNumber}${pipeName ? `: ${pipeName}` : ''}`;

    return {
      label: displayValue,
      pipeName,
      jobNumber,
      value: displayValue,
    };
  });
}

export function buildAllStatusJobOptionsFromLimits(
  limitsData: LimitsDataForManualStatus | null | undefined
): CombinedStatusJobOption[] {
  const automatedJobs = buildAutomatedJobOptionsFromLimits(limitsData);
  const manualJobs = mapApiResponseToJobNumbers(mapLimitsDataToManualStatusConfiguration(limitsData));
  return buildCombinedStatusJobOptions(automatedJobs, manualJobs);
}

export function resolveStatusJobOption(
  options: CombinedStatusJobOption[],
  valueOrLabel: string | undefined
): CombinedStatusJobOption | undefined {
  if (!valueOrLabel) return undefined;

  const directMatch = options.find(
    (option) => option.value === valueOrLabel || option.label === valueOrLabel
  );
  if (directMatch) return directMatch;

  const parsed = parseManualJobSelectValue(valueOrLabel);
  if (parsed.jobNumber) {
    return options.find(
      (option) =>
        option.jobNumber.trim().toLowerCase() === parsed.jobNumber.trim().toLowerCase() &&
        (parsed.machine === MANUAL_WELDING_MACHINE_ID ? option.isManual : !option.isManual)
    );
  }

  return undefined;
}

/** Stable cache/save key — uses parsed job parts, not the display string. */
export function getStatusJobConfigurationKey(
  options: CombinedStatusJobOption[],
  jobNumberValue: string
): string {
  if (!jobNumberValue) return '';

  const jobOption = resolveStatusJobOption(options, jobNumberValue);
  if (jobOption) {
    const weldingSystem = jobOption.label.split(':')[0]?.trim().toLowerCase() || '';
    if (weldingSystem === 'm500' && jobOption.pipeName) {
      return `${jobOption.jobNumber}:${jobOption.pipeName}`;
    }
    return jobOption.jobNumber;
  }

  return parseManualJobSelectValue(jobNumberValue).jobNumber || jobNumberValue;
}

export function isManualStatusJob(label: string, value?: string): boolean {
  const labelMachine = label.split(':')[0]?.trim().toLowerCase();
  if (labelMachine === MANUAL_WELDING_MACHINE_ID) return true;
  if (value?.toLowerCase().startsWith(`${MANUAL_WELDING_MACHINE_ID}:`)) return true;
  return parseManualJobSelectValue(value ?? label).machine === MANUAL_WELDING_MACHINE_ID;
}

export function toManualJobSelectValue(jobNumber: string): string {
  return formatManualJobSelectValue(MANUAL_WELDING_MACHINE_ID, jobNumber);
}

export function buildCombinedStatusJobOptions(
  automatedJobs: Array<{ label: string; value: string; jobNumber: string; pipeName: string }>,
  manualJobs: Array<{ value: string; label: string }>
): CombinedStatusJobOption[] {
  const manualEntries: CombinedStatusJobOption[] = manualJobs.map((job) => ({
    label: job.label,
    value: toManualJobSelectValue(job.value),
    jobNumber: job.value,
    pipeName: '',
    isManual: true,
  }));

  const manualJobNumbers = new Set(
    manualEntries.map((job) => job.jobNumber.trim().toLowerCase())
  );

  const automatedEntries: CombinedStatusJobOption[] = automatedJobs
    .filter((job) => !isManualStatusJob(job.label, job.value))
    .filter((job) => !manualJobNumbers.has(job.jobNumber.trim().toLowerCase()))
    .map((job) => ({
      ...job,
      isManual: false,
    }));

  return [...manualEntries, ...automatedEntries];
}
